import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Trophy, LogOut, LayoutDashboard, Target, Menu, X, Shield, History, LogIn, ChevronDown, Archive
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import useCompetitionStatus from '../hooks/useCompetitionStatus';

// Full catalogue — order preserved. Each sport gets promoted/demoted between
// the active nav and the "Closed Competitions" dropdown based on backend
// state (see `useCompetitionStatus`).
const DASHBOARD_ITEM = { label: 'Dashboard', path: '/', icon: LayoutDashboard };
const SPORT_TIPPING = {
  AFL: { label: 'AFL Tipping', longLabel: 'AFL Tipping 2026', path: '/tipping/AFL', icon: Target, color: '#E00000' },
  NRL: { label: 'NRL Tipping', longLabel: 'NRL Tipping 2026', path: '/tipping/NRL', icon: Target, color: '#007A33' },
  EPL: { label: 'EPL Tipping', longLabel: 'EPL Tipping 2026', path: '/tipping/EPL', icon: Target, color: '#37003C' },
  AL:  { label: 'A-League',    longLabel: 'A-League 2026/27', path: '/tipping/AL',  icon: Target, color: '#F0423E' },
  WC:  { label: 'World Cup',   longLabel: 'World Cup 2026',   path: '/tipping/WC',  icon: Target, color: '#4338CA' },
};
const TRAILING_ITEMS = [
  { label: 'Leaderboards', path: '/leaderboards', icon: Trophy, color: '#FFB81C' },
  { label: 'History', path: '/history', icon: History },
];
// Rendered order for the active bar — sports the app tips on, in comp order.
const SPORT_ORDER = ['AFL', 'NRL', 'EPL', 'AL', 'WC'];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const closedRef = useRef(null);
  const compStatus = useCompetitionStatus();

  // Derive active + closed nav lists from backend signal (with safe defaults
  // so first paint isn't blank).
  const closedSet = new Set(compStatus?.closed_sports || []);
  const activeSports = SPORT_ORDER.filter(s => !closedSet.has(s));
  const closedSports = SPORT_ORDER.filter(s => closedSet.has(s));
  const ACTIVE_NAV_ITEMS = [
    DASHBOARD_ITEM,
    ...activeSports.map(s => ({ ...SPORT_TIPPING[s] })),
    ...TRAILING_ITEMS,
  ];
  const CLOSED_COMPETITIONS = closedSports.map(s => ({
    label: SPORT_TIPPING[s].longLabel,
    path: SPORT_TIPPING[s].path,
    color: SPORT_TIPPING[s].color,
  }));

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (closedRef.current && !closedRef.current.contains(e.target)) {
        setClosedOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const closedActive = CLOSED_COMPETITIONS.some((c) => isActive(c.path));

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
            <img
              src="/logo-mark.png"
              alt="Tipping Central"
              className="h-10 w-auto"
              width={40}
              height={40}
            />
            <span className="hidden sm:inline text-lg font-bold uppercase tracking-tight text-[#1A1A1A] leading-none">Tipping Central</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 mx-6" data-testid="desktop-nav">
            {ACTIVE_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    active
                      ? 'bg-[#007AFF]/10 text-[#007AFF]'
                      : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="w-4 h-4" style={item.color && active ? { color: item.color } : {}} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* Closed Competitions dropdown — only when at least one exists */}
            {CLOSED_COMPETITIONS.length > 0 && (
            <div className="relative" ref={closedRef}>
              <button
                type="button"
                onClick={() => setClosedOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                  closedActive
                    ? 'bg-[#007AFF]/10 text-[#007AFF]'
                    : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                }`}
                data-testid="nav-closed-competitions"
                aria-expanded={closedOpen}
              >
                <Archive className="w-4 h-4" />
                <span>Closed Competitions</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${closedOpen ? 'rotate-180' : ''}`} />
              </button>
              {closedOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-sm shadow-lg py-1 z-50"
                  data-testid="closed-competitions-menu"
                >
                  {CLOSED_COMPETITIONS.map((c) => {
                    const active = isActive(c.path);
                    return (
                      <Link
                        key={c.path}
                        to={c.path}
                        onClick={() => setClosedOpen(false)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                          active
                            ? 'bg-[#007AFF]/10 text-[#007AFF] font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`closed-item-${c.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                        <span>{c.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {user && (() => {
              const active = isActive('/admin');
              return (
                <Link
                  to="/admin"
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    active ? 'bg-[#007AFF]/10 text-[#007AFF]' : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                  }`}
                  data-testid="nav-admin"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              );
            })()}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="text-gray-500 text-sm hidden md:block">{user.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-100"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="navbar-login-link">
                <Button
                  size="sm"
                  className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
                  data-testid="navbar-login-btn"
                >
                  <LogIn className="w-4 h-4 mr-1.5" />
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-gray-600 hover:bg-gray-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="lg:hidden border-t border-gray-200 py-3 space-y-1" data-testid="mobile-nav">
            {ACTIVE_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                    active
                      ? 'bg-[#007AFF]/10 text-[#007AFF]'
                      : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="w-4 h-4" style={item.color && active ? { color: item.color } : {}} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            {/* Mobile: closed competitions section */}
            {CLOSED_COMPETITIONS.length > 0 && (
            <div className="pt-2 mt-2 border-t border-gray-100">
              <div className="px-3 py-1 text-xs uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-1.5">
                <Archive className="w-3 h-3" />
                Closed Competitions
              </div>
              {CLOSED_COMPETITIONS.map((c) => {
                const active = isActive(c.path);
                return (
                  <Link
                    key={c.path}
                    to={c.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                      active ? 'bg-[#007AFF]/10 text-[#007AFF]' : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                    }`}
                    data-testid={`mobile-closed-${c.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span>{c.label}</span>
                  </Link>
                );
              })}
            </div>
            )}
            {user && (
              <Link
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive('/admin') ? 'bg-[#007AFF]/10 text-[#007AFF]' : 'text-gray-600 hover:text-[#1A1A1A] hover:bg-gray-100'
                }`}
                data-testid="mobile-nav-admin"
              >
                <Shield className="w-4 h-4" />
                <span>Admin</span>
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
